import React from 'react';
import {
  AppstoreOutlined,
  BankOutlined,
  BookOutlined,
  DashboardOutlined,
  GlobalOutlined,
  RobotOutlined,
  SettingOutlined,
  TeamOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { AppFooterColumn, SideNavItem } from '../UI/nav';
import { CONTROL_BRIDGE_HOME_PATH } from '../../utils/controlBridgeRoutes';
import type { AppNavAccess } from './appNavAccess';
import { APP_NAV_FEATURE_CODES } from './appNavFeatureCodes';

export const DEFAULT_FOOTER_COLUMNS: AppFooterColumn[] = [
  {
    title: 'Product',
    links: [
      { label: 'Control Bridge', href: CONTROL_BRIDGE_HOME_PATH },
      { label: 'Website Resources', href: '/resources' },
    ],
  },
  {
    title: 'Account',
    links: [{ label: 'Sign in', href: '/login' }],
  },
];

/** Primary authenticated app navigation (legacy routes remain in route registry for deep links). */
export function buildAppNavItems(access: AppNavAccess): SideNavItem[] {
  const items: SideNavItem[] = [
    {
      key: 'dashboard',
      label: 'Control Bridge',
      icon: React.createElement(DashboardOutlined),
      href: CONTROL_BRIDGE_HOME_PATH,
    },
    // Company artifacts (/artifacts) stays routable for deep links / platform tools but is
    // intentionally omitted from primary nav — surface is still in development and may be
    // superseded by Control Bridge / nested company-model navigation.
    {
      key: 'resources',
      label: 'Website Resources',
      icon: React.createElement(BookOutlined),
      href: '/resources',
    },
  ];

  if (access.features[APP_NAV_FEATURE_CODES.primeArtifacts]) {
    items.push({
      key: 'prime-artifacts',
      label: 'Prime artifacts',
      icon: React.createElement(SettingOutlined),
      href: '/prime/artifacts',
    });
  }

  if (access.features[APP_NAV_FEATURE_CODES.artifactConfiguration]) {
    items.push({
      key: 'configuration',
      label: 'Configuration',
      icon: React.createElement(SettingOutlined),
      children: [
        { key: 'artifact-types', label: 'Artifact types', href: '/artifact-types' },
        { key: 'state-templates', label: 'State templates', href: '/state-templates' },
        { key: 'option-catalogs', label: 'Option catalogs', href: '/option-catalogs' },
        {
          key: 'skills',
          label: 'Skills taxonomy',
          icon: React.createElement(TeamOutlined),
          href: '/skills',
        },
      ],
    });
  }

  // Company administration is the selected-company menu. Cross-tenant Users,
  // subscriptions, membership links, and the artifact tree manager stay on
  // Platform administration (co-kfo-poi-iss-prod-admin-surfaces-customer-nav-20260831).
  const companyAdminChildren: SideNavItem[] = [];
  if (access.canManageCompany) {
    companyAdminChildren.push(
      { key: 'company-admin-users', label: 'Users', href: '/company-admin/users' },
      {
        key: 'company-admin-subscriptions',
        label: 'Subscriptions',
        href: '/company-admin/subscriptions',
      },
      { key: 'company-admin-models', label: 'Models', href: '/company-admin/models' },
      { key: 'company-admin-billing', label: 'Billing', href: '/company-admin/billing' },
    );
  }
  companyAdminChildren.push({
    key: 'session-schedule',
    label: 'Session schedule',
    href: access.canManageCompany ? '/company-admin/sessions' : '/upcoming-sessions',
  });

  if (companyAdminChildren.length > 0) {
    items.push({
      key: 'admin-company',
      label: 'Company administration',
      icon: React.createElement(AppstoreOutlined),
      children: companyAdminChildren,
    });
  }

  if (access.isPlatformAdmin) {
    items.push({
      key: 'admin-platform',
      label: 'Platform administration',
      icon: React.createElement(GlobalOutlined),
      children: [
        {
          key: 'user-management',
          label: 'Users',
          icon: React.createElement(UserOutlined),
          href: '/user-management',
        },
        {
          key: 'company-management',
          label: 'Companies',
          icon: React.createElement(BankOutlined),
          href: '/company-management',
        },
        {
          key: 'subscription-management',
          label: 'Subscriptions',
          href: '/subscription-management',
        },
        { key: 'company-instances', label: 'User-company links', href: '/company-instances' },
        {
          key: 'artifact-tree-manager',
          label: 'Artifact tree manager',
          href: '/admin/artifact-tree-manager',
        },
        {
          key: 'subscription-catalog',
          label: 'Subscription catalog',
          href: '/platform/subscriptions-catalog',
        },
        {
          key: 'subscription-types',
          label: 'Subscription types',
          href: '/subscription-types-management',
        },
        {
          key: 'admin-resources',
          label: 'Website Resources admin',
          icon: React.createElement(BookOutlined),
          href: '/admin/resources',
        },
        {
          key: 'admin-bridge-session-schedule',
          label: 'Bridge session schedule',
          href: '/admin/bridge-session-schedule',
        },
        {
          key: 'global-properties',
          label: 'Global properties',
          icon: React.createElement(GlobalOutlined),
          href: '/admin/global-properties',
        },
        { key: 'krybot', label: 'Krybot', icon: React.createElement(RobotOutlined), href: '/krybot' },
        {
          key: 'gpt-integration',
          label: 'GPT assistant',
          icon: React.createElement(RobotOutlined),
          href: '/gpt-integration',
        },
        {
          key: 'architecture-agent',
          label: 'Architecture agent',
          icon: React.createElement(ToolOutlined),
          href: '/architecture-agent',
        },
      ],
    });
  }

  return items;
}

/** Map pathname to active side-nav key (longest prefix wins). */
export function resolveActiveNavKey(pathname: string, items: SideNavItem[]): string | null {
  if (pathname.includes('/control-bridge')) {
    return 'dashboard';
  }

  const flat: Array<{ key: string; href?: string }> = [];
  for (const item of items) {
    flat.push(item);
    for (const child of item.children ?? []) {
      flat.push(child);
    }
  }
  let best: { key: string; len: number } | null = null;
  for (const row of flat) {
    if (!row.href) continue;
    if (pathname === row.href || pathname.startsWith(`${row.href}/`)) {
      const len = row.href.length;
      if (!best || len > best.len) {
        best = { key: row.key, len };
      }
    }
  }
  return best?.key ?? null;
}

export function hrefForNavKey(key: string, items: SideNavItem[]): string | undefined {
  for (const item of items) {
    if (item.key === key) return item.href;
    for (const child of item.children ?? []) {
      if (child.key === key) return child.href;
    }
  }
  return undefined;
}

/** Onboarding intake — header only; hide SideNav / ChatPanel so prospector chat is the sole surface. */
export function isOnboardingIntakePath(pathname: string): boolean {
  return /^\/onboarding(\/|$)/.test(pathname);
}

/** Routes that use header only (no side nav / app footer). */
export function isMinimalChromePath(pathname: string): boolean {
  return /^\/(login|register|forgot-password|reset-password)(\/|$)/.test(pathname);
}
